import {
  ApartmentOutlined,
  BarChartOutlined,
  BellOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  FieldTimeOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { MenuDataItem as ProMenuDataItem } from '@ant-design/pro-components';
import React from 'react';
import { hrMenuGroupConfig } from '../constants/menuGroups';

export type HrMenuDataItem = ProMenuDataItem & {
  children?: HrMenuDataItem[];
};

const centerStandalonePaths = [
  '/zy-hr/performance-center',
  '/zy-hr/training-learning-center',
  '/zy-hr/career-planning',
  '/zy-hr/talent-inventory',
];

const cloneMenuItem = (item: HrMenuDataItem): HrMenuDataItem => ({
  ...item,
  children: item.children?.map(cloneMenuItem),
});

const groupedMenuIcons: Record<string, React.ReactNode> = {
  dashboard: <DashboardOutlined />,
  team: <TeamOutlined />,
  fieldTime: <FieldTimeOutlined />,
  wallet: <CreditCardOutlined />,
  safety: <SafetyCertificateOutlined />,
  apartment: <ApartmentOutlined />,
  bell: <BellOutlined />,
  barChart: <BarChartOutlined />,
};

export const hrRoutePermissionEntries: Array<[string, string]> = [];

export const getHrRouteRequiredPermission = (..._args: unknown[]) => undefined;

export const canAccessHrRoute = (..._args: unknown[]) => true;

export const filterHrMenuDataByPermissions = (
  menuData: HrMenuDataItem[],
  ..._args: unknown[]
): HrMenuDataItem[] => {
  const filterItem = (item: HrMenuDataItem): HrMenuDataItem | null => {
    if (item.hideInMenu) {
      return null;
    }
    const children = item.children?.map(filterItem).filter(Boolean) as
      | HrMenuDataItem[]
      | undefined;

    if (Array.isArray(item.children) && (!children || children.length === 0)) {
      return null;
    }

    return {
      ...item,
      children,
    };
  };

  return menuData.map(filterItem).filter(Boolean) as HrMenuDataItem[];
};

export const regroupHrMenuData = (
  menuData: HrMenuDataItem[],
): HrMenuDataItem[] => {
  const cloned = menuData.map(cloneMenuItem);
  const pathMap = new Map<string, HrMenuDataItem>();
  const rootItems: HrMenuDataItem[] = [];
  const groupKeys = new Set(hrMenuGroupConfig.map((group) => group.key));

  cloned.forEach((item) => {
    if (item.path) {
      pathMap.set(item.path, item);
    }
    rootItems.push(item);
  });

  const groupedChildPaths = new Set(
    hrMenuGroupConfig.flatMap((group) => group.childPaths),
  );
  const ungroupedRootItems = rootItems.filter(
    (item) => !item.path || !groupedChildPaths.has(item.path),
  );

  const groupedItems = hrMenuGroupConfig
    .map((group) => {
      const children = group.childPaths
        .map((path) => pathMap.get(path))
        .filter(Boolean) as HrMenuDataItem[];
      if (children.length === 0) {
        return null;
      }
      return {
        path: group.key,
        name: group.name,
        locale: group.locale,
        icon: groupedMenuIcons[group.icon],
        children,
      };
    })
    .filter(Boolean) as HrMenuDataItem[];

  const insertedCenterKeys = new Set(groupedItems.map((item) => item.path));
  const centerStandaloneItems = ungroupedRootItems.filter(
    (item) =>
      item.path?.startsWith('/zy-hr/') &&
      centerStandalonePaths.includes(item.path),
  );
  const nonCenterItems = ungroupedRootItems.filter(
    (item) =>
      Boolean(item.name) &&
      (!item.path ||
        (!groupKeys.has(item.path) &&
          !centerStandalonePaths.includes(item.path))),
  );

  return [
    ...groupedItems,
    ...centerStandaloneItems.filter(
      (item) => !insertedCenterKeys.has(item.path),
    ),
    ...nonCenterItems,
  ];
};
